/**
 * Copy HTML for WordPress Classic Editor (Visual tab).
 * Uses ClipboardItem promises so fetch + copy works in one click (Chrome/Safari).
 */

const ENTRY_TYPE_LABELS_ZH = {
  Sermon: '講道',
  SundaySchool: '主日學',
  Event: '活動'
}

function escapeHtml(str) {
  if (str == null || str === '') return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function wpTypeLine(text) {
  return `<p><font size="5" color="#cc0000"><strong>${text}</strong></font></p>`
}

function wpMainLine(innerHtml) {
  return `<p><font size="4">${innerHtml}</font></p>`
}

/**
 * Build copy text for a single entry (card 📋) — no Zoom/YouTube, no API calls.
 */
export function buildQuickEntryCopy(entry, eventDate) {
  const typeLabel = ENTRY_TYPE_LABELS_ZH[entry.type] || entry.type
  const plainLines = [typeLabel, '']
  const htmlParts = [wpTypeLine(typeLabel)]

  if (eventDate) {
    plainLines.push(`  日期：${eventDate}`)
    htmlParts.push(wpMainLine(`日期：${escapeHtml(eventDate)}`))
  }

  plainLines.push(`  標題：${entry.title}`)
  htmlParts.push(wpMainLine(`標題：${escapeHtml(entry.title)}`))

  if (entry.time) {
    plainLines.push(`  時間：${entry.time}`)
    htmlParts.push(wpMainLine(`時間：${escapeHtml(entry.time)}`))
  }
  if (entry.speaker) {
    plainLines.push(`  講員：${entry.speaker}`)
    htmlParts.push(wpMainLine(`講員：${escapeHtml(entry.speaker)}`))
  }
  if (entry.scripture) {
    plainLines.push(`  經文：${entry.scripture}`)
    htmlParts.push(wpMainLine(`經文：${escapeHtml(entry.scripture)}`))
  }
  if (entry.description) {
    plainLines.push(`  描述：${entry.description}`)
    htmlParts.push(wpMainLine(`描述：${escapeHtml(entry.description)}`))
  }

  return { html: htmlParts.join('\n'), text: plainLines.join('\n') }
}

function wpChurchLine(name) {
  return `<p><br></p><p><font size="4"><strong>【${escapeHtml(name)}】</strong></font></p>`
}

/** Combine API copy sections for one church (same content as 複製文字全部, one card). */
export function buildChurchCopyFromSections(sections, churchName) {
  const list = sections || []
  const plainLines = [`【${churchName}】`]
  const htmlParts = [wpChurchLine(churchName)]

  for (const section of list) {
    plainLines.push('')
    plainLines.push(section.text)
    htmlParts.push(section.html)
    htmlParts.push('<p><br></p>')
  }

  return {
    html: htmlParts.join('\n'),
    text: plainLines.join('\n').trim()
  }
}

export async function copyRichHtmlForWordPress(html, plainText = '') {
  const plain = plainText || stripHtml(html)
  if (copyRichHtmlSync(html, plain)) return
  await writeClipboardBlobs(
    new Blob([wrapForClipboardHtml(html)], { type: 'text/html' }),
    new Blob([plain], { type: 'text/plain' })
  )
}

/**
 * Load content async then copy — keeps user-gesture chain via ClipboardItem promises.
 * @param {() => Promise<{ html: string, text: string }>} loadContent
 */
export async function copyRichHtmlWithLazyLoad(loadContent) {
  const dataPromise = loadContent()

  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': dataPromise.then(({ html }) =>
            new Blob([wrapForClipboardHtml(html || '')], { type: 'text/html' })
          ),
          'text/plain': dataPromise.then(({ html, text }) =>
            new Blob([text || stripHtml(html || '')], { type: 'text/plain' })
          )
        })
      ])
      return
    } catch (err) {
      console.warn('ClipboardItem lazy write failed:', err)
    }
  }

  const { html, text } = await dataPromise
  const plain = text || stripHtml(html || '')
  if (copyRichHtmlSync(html, plain)) return

  await writeClipboardBlobs(
    html ? new Blob([wrapForClipboardHtml(html)], { type: 'text/html' }) : null,
    new Blob([plain], { type: 'text/plain' })
  )
}

function copyRichHtmlSync(html, plainText) {
  if (html && copyViaSelection(html)) return true
  if (copyViaTextarea(plainText)) return true
  return false
}

async function writeClipboardBlobs(htmlBlob, plainBlob) {
  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined' && htmlBlob) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': plainBlob
        })
      ])
      return
    } catch (err) {
      console.warn('ClipboardItem write failed:', err)
    }
  }

  if (navigator.clipboard?.writeText) {
    const plain = await plainBlob.text()
    await navigator.clipboard.writeText(plain)
    return
  }

  const plain = await plainBlob.text()
  if (!copyViaTextarea(plain)) {
    throw new Error('Clipboard access denied')
  }
}

function copyViaSelection(html) {
  const el = document.createElement('div')
  el.contentEditable = 'true'
  el.innerHTML = html
  el.style.position = 'fixed'
  el.style.left = '-9999px'
  el.style.top = '0'
  el.style.opacity = '0'
  document.body.appendChild(el)

  const range = document.createRange()
  range.selectNodeContents(el)
  const selection = window.getSelection()
  selection.removeAllRanges()
  selection.addRange(range)

  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }

  selection.removeAllRanges()
  document.body.removeChild(el)
  return ok
}

function copyViaTextarea(text) {
  const el = document.createElement('textarea')
  el.value = text
  el.setAttribute('readonly', '')
  el.style.position = 'fixed'
  el.style.left = '-9999px'
  document.body.appendChild(el)
  el.select()
  el.setSelectionRange(0, text.length)

  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }

  document.body.removeChild(el)
  return ok
}

function stripHtml(html) {
  return String(html).replace(/<[^>]+>/g, '')
}

function wrapForClipboardHtml(fragment) {
  return [
    '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>',
    '<!--StartFragment-->',
    fragment,
    '<!--EndFragment-->',
    '</body></html>'
  ].join('')
}
